import { z } from 'zod';
import {
  RFI_STATUS_LABEL,
  RfiSchema,
  canTransitionRfi,
  inAnswerOrder,
  type Rfi,
  type RfiStatus,
} from '../../domain/entities/rfi.ts';
import { linkFields } from '../../domain/entities/file.ts';
import type { RecordRef } from '../../domain/entities/notification.ts';
import { newId } from '../../domain/id.ts';
import { expectUnchanged, updateWrite } from '../../ports/batch.ts';
import { NotFoundError } from '../../ports/repository.ts';
import type { ClockPort } from '../../ports/clock-port.ts';
import type { DataPort } from '../../ports/data-port.ts';
import type { Query, Unsubscribe } from '../../ports/query.ts';

/**
 * Requests for information (SCRUM-144, P8-11).
 *
 * The module is small because an RFI is small: a question, who it is for,
 * when it is needed, and an answer. What makes it worth its own module is
 * what it points at — a change order, a selection, a schedule item — and
 * that the person asking usually cannot change the thing they are asking
 * about.
 */
export class RfiError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RfiError';
  }
}

export interface RfiContext {
  readonly data: DataPort;
  readonly clock: ClockPort;
  readonly companyId: string;
  readonly jobId: string;
  readonly uid: string;
}

const scope = (context: RfiContext) => ({
  companyId: context.companyId,
  jobId: context.jobId,
});

const repo = (context: RfiContext) => context.data.collection<Rfi>(scope(context), 'rfis');

/**
 * The query an external member is allowed to issue.
 *
 * The same arrangement as warranty claims and spec sections: `allow list`
 * is evaluated per returned document, so a sub asking for every RFI on
 * the job is refused on the first one that is not theirs, and asking for
 * their own succeeds. A trade sees what they raised; what was *put to*
 * them is a second query, because Firestore has no `or`.
 */
export function ownRfiQuery(uid: string): Query {
  return { where: [{ field: 'askedBy', op: '==', value: uid }] };
}

export function rfisForContactQuery(contactId: string): Query {
  return { where: [{ field: 'directedToContactId', op: '==', value: contactId }] };
}

export async function listRfis(context: RfiContext): Promise<Rfi[]> {
  const page = await repo(context).list();
  return inAnswerOrder(page.items);
}

/**
 * What one external member may see, in one list.
 *
 * Two queries merged rather than one, because Firestore cannot express
 * "mine **or** put to me" in a single filter and a rule cannot widen a
 * query. Merged by id so an RFI a sub both raised and was sent appears
 * once — which happens whenever the office redirects a question back to
 * the trade that asked it.
 */
export async function listRfisFor(
  context: RfiContext,
  uid: string,
  contactId?: string,
): Promise<Rfi[]> {
  const pages = await Promise.all([
    repo(context).list(ownRfiQuery(uid)),
    contactId ? repo(context).list(rfisForContactQuery(contactId)) : Promise.resolve({ items: [] }),
  ]);
  const byId = new Map<string, Rfi>();
  for (const page of pages) for (const one of page.items) byId.set(one.id, one);
  return inAnswerOrder([...byId.values()]);
}

export function subscribeToRfis(
  context: RfiContext,
  onChange: (rfis: readonly Rfi[]) => void,
  onError: (cause: Error) => void,
): Unsubscribe {
  return repo(context).subscribe({}, (page) => onChange(inAnswerOrder(page.items)), onError);
}

export async function getRfi(context: RfiContext, id: string): Promise<Rfi> {
  const found = await repo(context).get(id);
  if (!found) throw new NotFoundError('rfis', id);
  return found;
}

export const AskInputSchema = z.object({
  subject: z.string().trim().min(1, 'Say what the question is about').max(200),
  question: z.string().trim().max(20_000).default(''),
  dueDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'A due date is a calendar date')
    .optional(),
  directedToContactId: z.string().optional(),
});
export type AskInput = z.input<typeof AskInputSchema>;

/**
 * Ask a question.
 *
 * `about` is the record the question is about, and it is how the count
 * beside a change order is found later. Optional, because a question
 * about the job rather than about any one record is still a question —
 * and refusing it would send it to a text message instead, where nothing
 * can chase it.
 */
export async function askRfi(
  context: RfiContext,
  input: AskInput,
  about: readonly RecordRef[] = [],
): Promise<Rfi> {
  const parsed = AskInputSchema.safeParse(input);
  if (!parsed.success) throw new RfiError(parsed.error.issues[0]!.message);

  const at = context.clock.now().toISOString();
  const rfi = RfiSchema.parse({
    id: newId(),
    companyId: context.companyId,
    jobId: context.jobId,
    subject: parsed.data.subject,
    question: parsed.data.question,
    status: 'open',
    ...(parsed.data.dueDate ? { dueDate: parsed.data.dueDate } : {}),
    ...(parsed.data.directedToContactId
      ? { directedToContactId: parsed.data.directedToContactId }
      : {}),
    askedBy: context.uid,
    askedAt: at,
    ...linkFields(about),
    createdAt: at,
    createdBy: context.uid,
    updatedAt: at,
    updatedBy: context.uid,
    version: 1,
  });
  await repo(context).create(rfi);
  return rfi;
}

async function write(context: RfiContext, existing: Rfi, patch: Partial<Rfi>): Promise<Rfi> {
  const updatedAt = context.clock.now().toISOString();
  await context.data.commit([
    updateWrite<Rfi>(
      scope(context),
      'rfis',
      existing.id,
      { ...patch, updatedAt, updatedBy: context.uid },
      expectUnchanged(existing),
    ),
  ]);
  return { ...existing, ...patch, updatedAt, updatedBy: context.uid };
}

/**
 * Answer it.
 *
 * **The answer is the transition.** There is no way to mark an RFI
 * answered without writing one, because "answered" with nothing to read
 * is the state this module exists to stop: the person who was blocked is
 * still blocked, and the list no longer says so.
 */
export async function answerRfi(context: RfiContext, rfi: Rfi, text: string): Promise<Rfi> {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    throw new RfiError('Write the answer. Marking it answered with nothing to read helps nobody.');
  }
  if (!canTransitionRfi(rfi.status, 'answered')) {
    throw new RfiError(`${rfi.subject} is ${RFI_STATUS_LABEL[rfi.status].toLowerCase()}.`);
  }
  return write(context, rfi, {
    status: 'answered',
    answer: { text: trimmed, at: context.clock.now().toISOString(), byUid: context.uid },
  });
}

/**
 * Send it back, because the answer did not answer it.
 *
 * **The old answer stays on the record.** Clearing it would lose what was
 * said, and what was said is usually why it is being reopened — somebody
 * reading the thread needs to see the answer that missed as much as the
 * question.
 */
export async function reopenRfi(context: RfiContext, rfi: Rfi): Promise<Rfi> {
  if (!canTransitionRfi(rfi.status, 'open')) {
    throw new RfiError(`${rfi.subject} is ${RFI_STATUS_LABEL[rfi.status].toLowerCase()}.`);
  }
  return write(context, rfi, { status: 'open' });
}

export async function closeRfi(context: RfiContext, rfi: Rfi): Promise<Rfi> {
  if (!canTransitionRfi(rfi.status, 'closed')) {
    throw new RfiError(`${rfi.subject} is already closed.`);
  }
  return write(context, rfi, { status: 'closed' });
}

/** Correct the wording, or move the date. Never the status. */
export async function editRfi(context: RfiContext, rfi: Rfi, input: AskInput): Promise<Rfi> {
  const parsed = AskInputSchema.safeParse(input);
  if (!parsed.success) throw new RfiError(parsed.error.issues[0]!.message);
  return write(context, rfi, {
    subject: parsed.data.subject,
    question: parsed.data.question,
    dueDate: parsed.data.dueDate ?? undefined,
    directedToContactId: parsed.data.directedToContactId ?? undefined,
  });
}

/**
 * Which status moves this RFI may make.
 *
 * Read from the table rather than written per screen, for the reason
 * warranty claims record at length: a screen that decided for itself
 * would eventually offer a move the use case refuses.
 */
export function movesFor(rfi: Pick<Rfi, 'status'>): readonly RfiStatus[] {
  return (['open', 'answered', 'closed'] as const).filter((to) => canTransitionRfi(rfi.status, to));
}
